import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { BaseTimelineEvent } from './types';
import { PANEL_TEXT_STYLES } from '../../../constants/text-styles';

interface BaseTimelineItemProps {
  event: BaseTimelineEvent;
  isLast?: boolean;
  showLine?: boolean;
  renderContent?: (event: BaseTimelineEvent) => React.ReactNode;
  className?: string;
}

export const BaseTimelineItem: React.FC<BaseTimelineItemProps> = ({
  event,
  isLast = false,
  showLine = true,
  renderContent,
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetails = Boolean(event.details?.length);

  const handleToggle = () => {
    if (hasDetails) {
      setIsExpanded(prev => !prev);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };


  return (
    <div className={cn('flex gap-5', className)}>
      {/* Timeline column - bubble and connecting line */}
      <div className="flex flex-col items-center shrink-0 w-4">
        {/* Timeline bubble */}
        <div className="w-4 h-4 rounded-full bg-indigo-50 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-indigo-500" />
        </div>
        {/* Connecting line */}
        {showLine && !isLast && (
          <div className="w-px bg-gray-200 flex-1" />
        )}
      </div>

      <div className="flex-1 min-w-0 pb-6">
        {renderContent ? (
          renderContent(event)
        ) : (
          <>
            <div
              className={cn(
                'flex items-center justify-between px-3 rounded-lg transition-colors',
                hasDetails && 'cursor-pointer hover:bg-muted',
                isExpanded && hasDetails && 'bg-muted'
              )}
              onClick={hasDetails ? handleToggle : undefined}
              role={hasDetails ? 'button' : undefined}
              aria-expanded={hasDetails ? isExpanded : undefined}
              tabIndex={hasDetails ? 0 : undefined}
              onKeyDown={hasDetails ? handleKeyDown : undefined}
            >
              <div className="flex flex-col min-w-0 flex-1">
                <div className={PANEL_TEXT_STYLES.cardValue}>
                  {event.title}
                </div>
                <div className={cn(PANEL_TEXT_STYLES.label, "mt-0.5 font-sans")}>
                  {event.timestamp}
                </div>
              </div>

              {hasDetails && (
                <ChevronRight
                  className={cn(
                    'size-3 text-muted-foreground transition-transform shrink-0 ml-2',
                    isExpanded && 'rotate-90'
                  )}
                />
              )}
            </div>

            {hasDetails && isExpanded && (
              <div className="mt-2 ml-3 space-y-2 bg-muted rounded-lg p-3">
                {event.details!.map((detail, detailIndex) => (
                  <div
                    key={`${event.id || event.title}-detail-${detailIndex}`}
                    className="flex items-center justify-between py-2"
                  >
                    <div className={PANEL_TEXT_STYLES.largeValue}>
                      {detail.action}
                    </div>
                    <div className={cn(PANEL_TEXT_STYLES.label, "ml-2 shrink-0")}>
                      {detail.timestamp}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
